import { useState, useCallback } from "react";
import { getDeviceFingerprint } from "@/lib/fingerprint";
import { getDeviceId } from "@/lib/deviceId";
import { supabasePublic } from "@/integrations/supabase/publicClient";

interface EntryResult {
  ok: boolean;
  awardedEntries: number;
  totalEntries?: number;
  totalEntriesForParticipant?: number; // legacy fallback
  participantReferralCode: string;
  completedMethodIds?: string[];
  alreadyCompleted?: boolean;
  error?: string;
  code?: string;
}

interface ParticipantState {
  id?: string;
  email: string | null;
  referralCode: string | null;
  totalEntries: number;
  completedMethodIds: string[];
}

interface EntryState {
  isLoading: boolean;
  isLoadingParticipant: boolean;
  participant: ParticipantState;
  lastError: string | null;
}

export function useEntry(contestSlug: string, referralCodeFromUrl?: string | null, captureSource?: string | null) {
  const [state, setState] = useState<EntryState>({
    isLoading: false,
    isLoadingParticipant: false,
    participant: {
      id: undefined,
      email: null,
      referralCode: null,
      totalEntries: 0,
      completedMethodIds: [],
    },
    lastError: null,
  });

  const [localCompleted, setLocalCompleted] = useState<Set<string>>(new Set());

  const loadParticipant = useCallback(
    async (email: string, createIfMissing = false, fields?: Record<string, any>): Promise<{ exists: boolean; id?: string; totalEntries: number; completedMethodIds: string[]; referralCode?: string }> => {
      setState((prev) => ({ ...prev, isLoadingParticipant: true, lastError: null }));

      try {
        const client_device_id = getDeviceId();
        const payload = {
          slug: contestSlug,
          email,
          create_if_missing: createIfMissing,
          client_device_id,
          fields,
          capture_source: captureSource || undefined,
        };
        console.log('// TEMP DEBUG payload to get-participant:', payload);
        // Use public client to avoid sending auth headers that trigger RLS scoping
        const { data, error } = await supabasePublic.functions.invoke("get-participant", {
          body: payload,
        });

        if (error) {
          // Try to extract error message from response body
          const errorMessage = error.context?.body?.error || error.message || "Failed to check participant";
          throw new Error(errorMessage);
        }

        if (!data.ok) {
          throw new Error(data.error || "Failed to check participant");
        }

        if (data.exists) {
          setState((prev) => ({
            ...prev,
            isLoadingParticipant: false,
            participant: {
              id: data.participant.id,
              email,
              referralCode: data.participant.referralCode,
              totalEntries: data.totalEntries,
              completedMethodIds: data.completedMethodIds || [],
            },
          }));

          return {
            exists: true,
            id: data.participant.id,
            totalEntries: data.totalEntries,
            completedMethodIds: data.completedMethodIds || [],
            referralCode: data.participant.referralCode,
          };
        } else {
          setState((prev) => ({
            ...prev,
            isLoadingParticipant: false,
            participant: {
              id: undefined,
              email,
              referralCode: null,
              totalEntries: 0,
              completedMethodIds: [],
            },
          }));

          return { exists: false, totalEntries: 0, completedMethodIds: [] };
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
        setState((prev) => ({ ...prev, isLoadingParticipant: false, lastError: errorMessage }));
        throw err;
      }
    },
    [contestSlug, captureSource]
  );

  const submitEntry = useCallback(
    async (entryMethodKey: string, entryMethodId: string, turnstileToken?: string): Promise<EntryResult> => {
      if (!state.participant.email) {
        return { ok: false, awardedEntries: 0, totalEntriesForParticipant: 0, participantReferralCode: "", error: "Email required" };
      }

      setState((prev) => ({ ...prev, isLoading: true, lastError: null }));

      try {
        const deviceFingerprint = await getDeviceFingerprint();
        const client_device_id = getDeviceId();

        // Use public client to avoid sending auth headers that trigger RLS scoping
        const { data, error } = await supabasePublic.functions.invoke("enter", {
          body: {
            slug: contestSlug,
            entryMethodKey,
            email: state.participant.email,
            participant_id: state.participant.id,
            ref: referralCodeFromUrl || undefined,
            deviceFingerprint,
            client_device_id,
            turnstile_token: turnstileToken,
          },
        });

        if (error) {
          // Try to extract error message from response body
          const body = (error as any).context?.body;
          const errorMessage = body?.error || error.message || "Failed to submit entry";
          const errorCode = body?.code;

          setState((prev) => ({ ...prev, isLoading: false, lastError: errorMessage }));
          return { ok: false, awardedEntries: 0, totalEntriesForParticipant: 0, participantReferralCode: "", error: errorMessage, code: errorCode };
        }

        if (!data.ok) {
          setState((prev) => ({ ...prev, isLoading: false, lastError: data.error }));
          return { ...data, code: data.code } as EntryResult;
        }

        if (data.ok || data.alreadyCompleted || data.already_completed) {
          setLocalCompleted((prev) => new Set([...prev, entryMethodId]));
        }

        setState((prev) => ({
          ...prev,
          isLoading: false,
          participant: {
            ...prev.participant,
            referralCode: data.participantReferralCode,
            totalEntries: data.totalEntries ?? data.totalEntriesForParticipant ?? prev.participant.totalEntries,
            completedMethodIds: data.completedMethodIds || [...prev.participant.completedMethodIds],
          },
          lastError: null,
        }));

        return data as EntryResult;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
        setState((prev) => ({ ...prev, isLoading: false, lastError: errorMessage }));
        return { ok: false, awardedEntries: 0, totalEntriesForParticipant: 0, participantReferralCode: "", error: errorMessage };
      }
    },
    [contestSlug, referralCodeFromUrl, state.participant.email, state.participant.id, state.participant.completedMethodIds] // Added strict dependencies
  );

  const setEmail = useCallback((email: string) => {
    setState((prev) => ({
      ...prev,
      participant: { ...prev.participant, email },
    }));
  }, []);

  const isMethodCompleted = useCallback(
    (methodId: string) => state.participant.completedMethodIds.includes(methodId),
    [state.participant.completedMethodIds]
  );

  return {
    ...state,
    email: state.participant.email,
    referralCode: state.participant.referralCode,
    totalEntries: state.participant.totalEntries,
    completedMethodIds: state.participant.completedMethodIds,
    submitEntry,
    loadParticipant,
    setEmail,
    isMethodCompleted,
  };
}
